import "./InsuranceSearchTab.scss";
import { AutoComplete, Button, Form, type FormInstance } from "antd";
import { ModalAppearanceProps } from "../modalProps";
import { TakePaymentRadioTabs } from "../TakePaymentRadioTabs/TakePaymentRadioTabs";
import { VyneForm } from "@vyne-shared/components/vyneForm/VyneForm";
import { VyneModal } from "@vyne-shared/components/vyneModal/VyneModal";
import { antdModalV5 } from "@ebay/nice-modal-react";
import { useInsuranceQuery } from "api/clients/useInsuranceQuery";
import { useSearchFlowContext } from "./useSearchFlow";
import { useState } from "react";
import type { ProvisionInsurancePaymentRequest } from "api/requests";
import type { TakePaymentTabType } from "../TakePaymentModal";

interface InsuranceSearchTabProps {
    /** Set the current tab in the Take Payment modal. */
    readonly setTakePaymentTab: (tab: TakePaymentTabType) => void;
}

/** Displays the Insurance Payment tab to search for an insurance carrier and take a payment. */
export const InsuranceSearchTab = ({ setTakePaymentTab }: InsuranceSearchTabProps) => {
    // insurance carrier form
    const [form] = Form.useForm<ProvisionInsurancePaymentRequest>();
    const { modal, isProvisioning } = useSearchFlowContext();

    return (
        <VyneModal
            {...antdModalV5(modal)}
            title="Take a Payment"
            footer={
                <InsuranceCarrierFooter
                    isProvisioning={isProvisioning}
                    onCancel={() => void modal.hide()}
                    onNext={form.submit}
                />
            }
            {...ModalAppearanceProps}
        >
            <TakePaymentRadioTabs setTakePaymentTab={setTakePaymentTab} takePaymentTab="insurance-payment" />
            <InsuranceCarrierSearch form={form} />
        </VyneModal>
    );
};

/** The Insurance Carrier Search form. */
const InsuranceCarrierSearch = ({ form }: { form: FormInstance<ProvisionInsurancePaymentRequest> }) => {
    const { insuranceValue, setInsuranceValue, data } = useSearchPatientField();
    const { onSelectInsuranceCarrier } = useSearchFlowContext();

    const options =
        data
            ?.sort((a, b) => {
                // sort by date updated descending
                return b.updateDate - a.updateDate;
            })
            .map((x) => ({
                label: x.name,
                value: x.name,
            })) ?? [];

    return (
        <VyneForm
            form={form}
            requiredMark={false}
            onFinish={(x) => {
                onSelectInsuranceCarrier(x.name);
            }}
        >
            <Form.Item label="Enter Insurance Carrier Name" name="name" data-testid="queryFullName" required>
                <AutoComplete
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- since it's first focusable item in modal, it is accessible
                    autoFocus={true}
                    classNames={{ popup: { root: "insurance-search-dropdown" } }}
                    value={insuranceValue}
                    onChange={(newInsuranceValue) => setInsuranceValue(newInsuranceValue)}
                    options={options}
                />
            </Form.Item>
        </VyneForm>
    );
};

function useSearchPatientField() {
    const [insuranceValue, setInsuranceValue] = useState("");
    const { data } = useInsuranceQuery(insuranceValue, 200);

    return { insuranceValue, setInsuranceValue, data } as const;
}

const InsuranceCarrierFooter = ({
    onCancel,
    onNext,
    isProvisioning,
}: {
    readonly onCancel: () => void;
    onNext: () => void;
    isProvisioning: boolean;
}) => (
    <>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" onClick={onNext} loading={isProvisioning}>
            Next
        </Button>
    </>
);
